// src/components/ProjectModal.js
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { colors, fonts, fontSizes, fontWeights, radii, space, duration, ease } from '../styles/tokens';
import { ButtonLink } from './Button';
import { StackTag, StackTagList } from './StackTag';
import { Icon } from './Icon';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const scaleIn = keyframes`from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(10, 10, 15, 0.92);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${space[4]};
  animation: ${fadeIn} ${duration.base} ${ease.soft};
`;

const Card = styled.div`
  position: relative;
  background: ${colors.bgElev};
  border: 1px solid rgba(100, 255, 218, 0.3);
  border-radius: ${radii['2xl']};
  padding: ${space[5]};
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${scaleIn} ${duration.base} ${ease.soft};
`;

const CloseButton = styled.button`
  position: absolute;
  top: ${space[3]};
  right: ${space[3]};
  font-family: ${fonts.mono};
  font-size: ${fontSizes.xs};
  color: ${colors.textDim};
  padding: ${space[1]} ${space[2]};
  border: 1px solid ${colors.border};
  border-radius: ${radii.sm};
  &:hover { color: ${colors.accent}; border-color: ${colors.accent}; }
`;

const ImageBox = styled.div`
  width: 100%;
  aspect-ratio: 16/10;
  border-radius: ${radii.lg};
  overflow: hidden;
  background: ${colors.bgHover};
  margin: ${space[3]} 0 ${space[4]};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Title = styled.h3`
  font-family: ${fonts.sans};
  font-size: ${fontSizes.xl};
  font-weight: ${fontWeights.bold};
  color: ${colors.text};
  margin-top: ${space[3]};
  margin-bottom: ${space[2]};
`;

const Description = styled.p`
  color: ${colors.textDim};
  font-size: ${fontSizes.base};
  line-height: 1.6;
  margin-bottom: ${space[4]};
`;

export function ProjectModal({ project, onClose }) {
  const cardRef = useRef(null);
  const prevFocusRef = useRef(null);

  useEffect(() => {
    prevFocusRef.current = document.activeElement;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    cardRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      prevFocusRef.current?.focus?.();
    };
  }, [onClose]);

  if (!project) return null;

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card ref={cardRef} role="dialog" aria-modal="true" aria-labelledby="modal-title" tabIndex={-1}>
        <CloseButton onClick={onClose} aria-label="Close">[ esc ]</CloseButton>
        <StackTagList>
          {project.stack.map((t) => <StackTag key={t}>{t}</StackTag>)}
        </StackTagList>
        <ImageBox>
          <img src={project.image} alt={project.title} />
        </ImageBox>
        <Title id="modal-title">{project.title}</Title>
        <Description>{project.description}</Description>
        <ButtonLink href={project.link} target="_blank" rel="noopener noreferrer">
          Visit site <Icon name="external" size={16} />
        </ButtonLink>
      </Card>
    </Overlay>
  );
}
